import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { filter, map, NEVER, Observable, of } from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import { finShare } from '@fcom/rx';
import { ButtonMode, ButtonSize, ButtonTheme, IconPosition, TabLayoutType, TabTheme } from '@fcom/ui-components';
import { isShortHaulFlight, LoaderType } from '@fcom/common';
import { equals } from '@fcom/core/utils';
import { Category, FinnairBoundItem, FinnairCart, FinnairOrder, FinnairServiceItem } from '@fcom/dapi/api/models';
import {
  BoundWithServices,
  RemovedServiceInfo,
  RemovedServiceProgress,
  SelectedServiceData,
  UpsellCategoriesForBound,
} from '@fcom/dapi/interfaces';

import { getServicesForBoundWithUpsell } from '../../../../utils/common-booking.utils';
import { SmpProduct } from '../product-list/product-list.component';
import { CartOrOrder } from '../../../../interfaces';

@Component({
  selector: 'fin-included-products',
  templateUrl: './included-products.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IncludedProductsComponent implements OnInit, OnChanges {
  readonly ButtonMode = ButtonMode;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly Category = Category;
  readonly IconPosition = IconPosition;
  readonly LoaderType = LoaderType;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TabLayoutType = TabLayoutType;
  readonly TabTheme = TabTheme;

  @Input() booking: FinnairCart | FinnairOrder;

  @Input() bound: FinnairBoundItem | undefined;

  @Input() showHeading = true;

  @Input() showTickets = false;

  @Input() showModifyButtons = false;

  @Input() showInformationModal = false;

  @Input() useCabinClass = false;

  @Input() titleTranslationKey;

  @Input() showAdditionalTitle = false;

  @Input() expandOpen = false;

  @Input() headerContentTranslationKey?: string;

  @Input() servicesToShow: FinnairServiceItem[];

  @Input() showAsPending = false;

  @Input() upsellInModal$: Observable<UpsellCategoriesForBound> = NEVER;

  @Input() headingClasses = 'font-heading-4';

  @Input() showUpsell = false;

  @Input() displaySeatReassignMessage = false;

  @Input() includeSeatActionButtons = false;

  @Input() isCarrierConnect = false;

  @Input() showNonAYSeats = false;

  @Input() includeAssignAtAirportStatus = false;

  @Input() showClearPendingSeatsButton = false;

  @Input() isShoppingCart = false;

  @Input() groupServicesByBound = false;

  @Input() products$: Observable<SmpProduct[]> = of([]);

  @Input() removeInProgress$: Observable<RemovedServiceProgress> = of({ removeInProgress: false });

  @Output()
  continuePurchase: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  removeProduct: EventEmitter<RemovedServiceInfo> = new EventEmitter<RemovedServiceInfo>();

  @Output()
  upsellClicked: EventEmitter<SelectedServiceData> = new EventEmitter<SelectedServiceData>();

  @Output()
  viewSeatmap: EventEmitter<void> = new EventEmitter();

  @Output()
  clearPendingSeats: EventEmitter<void> = new EventEmitter();

  servicesForBounds: BoundWithServices[];

  headerContents$: Observable<string[]>;

  isShortHaulFlight: boolean;

  constructor(private languageService: LanguageService) {}

  ngOnInit(): void {
    this.updateServices();

    this.headerContents$ = this.headerContentTranslationKey
      ? this.languageService.translate(this.headerContentTranslationKey).pipe(
          map((content) => (Array.isArray(content) ? content : [content])),
          filter(Boolean),
          finShare()
        )
      : NEVER;

    this.isShortHaulFlight = this.isShortHaul(this.booking);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !equals(changes['showUpsell']?.currentValue, changes['showUpsell']?.previousValue) ||
      !equals(changes['showUpsellPaymentButton']?.currentValue, changes['showUpsellPaymentButton']?.previousValue) ||
      !equals(changes['servicesToShow']?.currentValue, changes['servicesToShow']?.previousValue) ||
      !equals(changes['booking']?.currentValue, changes['booking']?.previousValue)
    ) {
      this.updateServices();
    }
  }

  private isShortHaul(booking: FinnairCart | FinnairOrder): boolean {
    const locations = Object.values(booking.locations);
    return isShortHaulFlight(locations[0], locations[1]);
  }

  private updateServices(): void {
    this.servicesForBounds = this.getServicesToShowForBounds(
      this.booking,
      this.showUpsell,
      this.servicesToShow,
      this.bound ? [this.bound] : this.booking.bounds,
      this.products$
    );
  }

  private getServicesToShowForBounds(
    cartOrOrder: CartOrOrder,
    showUpsell: boolean,
    servicesToShow: FinnairServiceItem[],
    bounds: FinnairBoundItem[],
    products$: Observable<SmpProduct[]> = of([])
  ): BoundWithServices[] {
    return bounds.map((bound) => {
      return {
        ...bound,
        services: getServicesForBoundWithUpsell(
          cartOrOrder,
          showUpsell,
          servicesToShow,
          cartOrOrder.services.servicesOrder,
          bound,
          cartOrOrder.serviceCatalog,
          products$
        ),
      } as BoundWithServices;
    });
  }

  onSelectProduct(serviceData: SelectedServiceData): void {
    this.upsellClicked.emit(serviceData);
  }

  onContinuePurchase(): void {
    this.continuePurchase.emit();
  }

  onRemoveProduct(event: RemovedServiceInfo, boundId: string): void {
    this.removeProduct.emit({
      ...event,
      boundId: boundId,
    });
  }
}
